import { useCallback, useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import {
    applyFilters,
    ErrorState,
    MatrixTable,
    SkeletonTable,
    useToast,
    type FilterField,
    type FilterValues,
    type MatrixColumn,
    type MatrixSection,
    type MatrixTableClassNames,
} from '@/components/ui';
import {
    fetchConsoleRoles,
    updateConsoleRole,
    type PermissionCatalog,
    type PermissionEntry,
} from '@/api/orgAdmin';
import { CaretIcon, FilterIcon, InfoIcon, LockIcon, SearchIcon, WarnIcon } from './icons';

export interface PermissionCatalogTabProps {
    query: UseQueryResult<PermissionCatalog>;
    /** Page header owns the "Tải về CSV" button; this tab owns the rows it exports. */
    exportRef: MutableRefObject<(() => void) | null>;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * The skin. `MatrixTable` ships no CSS (Tier-1), so the frame-62 values stay
 * here in `global.css` beside the rest of that handoff — the component owns
 * structure and behaviour, this owns the look.
 */
const MATRIX_CLASSES: MatrixTableClassNames = {
    // The component's own root wrapper sits between `.pm` and the scroll region,
    // so it has to carry the height chain too — without it the wrapper grows to
    // content height and pushes the save bar out of the card.
    root: 'pm-tablewrap',
    scroll: 'pm-scroll',
    table: 'pm-table',
    cornerHeader: 'pm-th-perm',
    columnHeader: 'pm-th-role',
    rowHeader: 'pm-td-perm',
    cell: 'pm-cell',
    sectionRow: 'pm-grouprow',
    sectionHeader: 'pm-td-perm',
    box: 'pm-box',
    boxOn: 'pm-box--on',
    boxPartial: 'pm-box--partial',
    boxLocked: 'pm-box--locked',
    empty: 'perm-empty',
};

/** One matrix column. Built-ins are columns you can read but not tick. */
interface Column {
    key: string;
    id: string | null;
    label: string;
    /**
     * EAA-1843: the header is ONE line now, so the column carries a state FLAG
     * rather than a caption string. It used to hold "dựng sẵn · 40" / "vừa tạo ·
     * 0" / "3 lượt gán · 12" / "đã ngưng · 2" on a second line. Three of those
     * four were counts the header does not need — but the fourth said the role
     * was DEACTIVATED, which is not decoration: such a role grants nothing and
     * its column is locked, so dropping the line wholesale would have hidden the
     * one fact that explains why the ticks are read-only. Kept as a flag, shown
     * inline; the counts are gone.
     */
    inactive: boolean;
    locked: boolean;
    permissions: Set<string>;
}

/**
 * EAA-1843: "Danh mục quyền" — zip-11 frame 62, rebuilt as an EDITABLE
 * quyền × vai trò matrix.
 *
 * This replaces the read-only catalog, and the reversal is the point. The old
 * screen answered "who can do what" and then sent you to another tab to change
 * it; one grid answers the question and takes the edit in the same place. The
 * frame's own lede says it: "Một bảng, tất cả vai trò. Tích thẳng vào ô."
 *
 * Three properties hold the design together:
 *
 * * **Built-in columns render but do not tick.** `super_admin` / `org_admin` are
 *   source-defined, not rows, so the API refuses to edit them. They stay visible
 *   because the whole value of a matrix is comparing a custom role against them
 *   — and they are drawn in a muted tone so "locked" is legible before you click.
 * * **Edits are a local draft until saved.** Ticking mutates a per-role draft and
 *   the save bar reports the delta. Nothing writes per click: a mis-click on this
 *   grid grants real authority to everyone holding that role.
 * * **The permission column and the first role column stay put on horizontal
 *   scroll.** With eight roles the grid scrolls, and a tick means nothing once
 *   you cannot see which row and which column it belongs to.
 */
export function PermissionCatalogTab({ query, exportRef }: PermissionCatalogTabProps) {
    const { t } = useTranslation();
    const can = useCan();
    const toast = useToast();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const canManage = can('console.roles.manage');

    const [search, setSearch] = useState('');
    const [group, setGroup] = useState<string | null>(null);
    const [sorted, setSorted] = useState<'asc' | 'desc' | null>(null);
    const [hideBuiltin, setHideBuiltin] = useState(false);
    /** roleId -> the codes it will hold once saved. Absent = untouched. */
    const [draft, setDraft] = useState<Record<string, Set<string>>>({});

    const rolesQuery = useQuery({
        queryKey: ['console-roles', accessToken],
        queryFn: () => fetchConsoleRoles(config.apiBase, accessToken as string),
        enabled: Boolean(accessToken),
        retry: false,
    });

    // Memoized: a bare `?? []` mints a new array each render, defeating every
    // memo below on each keystroke.
    const groups = useMemo(() => query.data?.groups ?? [], [query.data]);
    const permissions = useMemo(() => query.data?.permissions ?? [], [query.data]);
    const custom = useMemo(() => rolesQuery.data?.custom ?? [], [rolesQuery.data]);

    /**
     * Built-in columns come from the CATALOG's role map, not from
     * `rolesQuery.builtin`: `/permissions` is served from the very structures the
     * server enforces against, so those two columns cannot drift from what a 403
     * would actually do.
     */
    const columns: Column[] = useMemo(() => {
        const builtin: Column[] = (query.data?.roles ?? []).map((r) => ({
            key: `builtin:${r.key}`,
            id: null,
            label: r.label,
            inactive: false,
            locked: true,
            permissions: new Set(r.permissions),
        }));
        const mine: Column[] = custom.map((r) => ({
            key: `role:${r.id}`,
            id: r.id,
            label: r.name,
            inactive: !r.is_active,
            // A deactivated role grants nothing; editing its set would be editing
            // something that is not in force, which is a confusing thing to allow.
            locked: !canManage || !r.is_active,
            permissions: new Set(r.permissions),
        }));
        return hideBuiltin ? mine : [...builtin, ...mine];
    }, [query.data, custom, hideBuiltin, canManage]);

    /** The set a cell should render — draft when touched, server copy otherwise. */
    function held(col: Column): Set<string> {
        return (col.id && draft[col.id]) || col.permissions;
    }

    // Chip counts come from the UNFILTERED list: a chip that recounted itself
    // against the active search would show "Tổ chức 0" while you type, which
    // reads as "there are none" rather than "none match".
    const countByGroup = useMemo(() => {
        const counts = new Map<string, number>();
        for (const p of permissions) counts.set(p.group, (counts.get(p.group) ?? 0) + 1);
        return counts;
    }, [permissions]);

    /**
     * The group chips and the search box are `DataTable`'s filter contract, not
     * a private one — `FilterField` + a `FilterValues` bag, resolved by the
     * shared `matchesFilters` engine. The chip row below is this screen's own
     * chrome (frame 62), but what a value MEANS is the kit's, so "filter by
     * group" behaves identically here and on every `DataTable`.
     */
    const filterFields: FilterField[] = useMemo(
        () => [
            {
                key: 'group',
                label: t('console.permissions.catalog.filterGroupLabel'),
                type: 'select',
                options: groups.map((g) => ({ label: g.label, value: g.key })),
            },
        ],
        [groups, t]
    );

    const filterValues: FilterValues = useMemo(() => ({ group: group ?? undefined }), [group]);

    /** What the free-text box looks at — see `MatrixRow.fields` below. */
    const SEARCH_KEYS = useMemo(() => ['code', 'label'], []);

    // Search covers the code AND the description — an operator arriving from a
    // 403 has the code; one composing a role has only the words.
    const visible = useMemo(
        () =>
            applyFilters(permissions, filterFields, filterValues, {
                search,
                searchKeys: SEARCH_KEYS,
            }),
        [permissions, filterFields, filterValues, search, SEARCH_KEYS]
    );

    // `sorted === null` means "server order", the curated view -> manage ->
    // destructive sequence. Only an explicit header click goes alphabetical.
    const rows = useMemo(
        () =>
            sorted === null
                ? visible
                : [...visible].sort((a, b) =>
                      sorted === 'asc' ? a.code.localeCompare(b.code) : b.code.localeCompare(a.code)
                  ),
        [visible, sorted]
    );

    /** Rows grouped under a section header, in the server's group order. */
    const sections = useMemo(() => {
        const map = new Map<string, PermissionEntry[]>();
        for (const p of rows) map.set(p.group, [...(map.get(p.group) ?? []), p]);
        return groups
            .filter((g) => map.has(g.key))
            .map((g) => ({ key: g.key, label: g.label, rows: map.get(g.key)! }));
    }, [groups, rows]);

    const dirtyRoles = useMemo(
        () =>
            custom.filter((r) => {
                const d = r.id && draft[r.id];
                if (!d) return false;
                if (d.size !== r.permissions.length) return true;
                return r.permissions.some((c) => !d.has(c));
            }),
        [custom, draft]
    );

    const changeCount = useMemo(() => {
        let n = 0;
        for (const r of dirtyRoles) {
            const d = draft[r.id as string];
            const server = new Set(r.permissions);
            for (const c of d) if (!server.has(c)) n += 1;
            for (const c of server) if (!d.has(c)) n += 1;
        }
        return n;
    }, [dirtyRoles, draft]);

    function toggle(col: Column, code: string) {
        if (col.locked || !col.id) return;
        setDraft((prev) => {
            const next = new Set(prev[col.id as string] ?? col.permissions);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return { ...prev, [col.id as string]: next };
        });
    }

    function toggleGroupCell(col: Column, codes: string[], allOn: boolean) {
        if (col.locked || !col.id) return;
        setDraft((prev) => {
            const next = new Set(prev[col.id as string] ?? col.permissions);
            for (const c of codes) {
                if (allOn) next.delete(c);
                else next.add(c);
            }
            return { ...prev, [col.id as string]: next };
        });
    }

    const save = useMutation({
        mutationFn: async () => {
            for (const r of dirtyRoles) {
                await updateConsoleRole(config.apiBase, accessToken as string, r.id as string, {
                    permissions: [...draft[r.id as string]],
                });
            }
            return dirtyRoles.length;
        },
        onSuccess: (n) => {
            toast.success(t('console.permissions.catalog.savedRolesToast', { count: n }));
            setDraft({});
            void queryClient.invalidateQueries({ queryKey: ['console-roles', accessToken] });
        },
        onError: (e: unknown) =>
            toast.error(
                e instanceof Error ? e.message : t('console.permissions.catalog.saveErrorToast')
            ),
    });

    function downloadCsv() {
        // Built from the FILTERED, SORTED rows and the VISIBLE columns: exporting
        // something other than what is on screen would be a quiet surprise.
        const header = [
            t('console.permissions.catalog.csvHeaderCode'),
            t('console.permissions.catalog.csvHeaderLabel'),
            t('console.permissions.catalog.csvHeaderGroup'),
            ...columns.map((c) => c.label),
            t('console.permissions.catalog.csvHeaderEndpoint'),
        ];
        const body = rows.map((p) => [
            p.code,
            p.label,
            p.group_label,
            ...columns.map((c) => (held(c).has(p.code) ? 'x' : '')),
            String(p.endpoint_count),
        ]);
        const escape = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
        const csv = [header, ...body].map((r) => r.map(escape).join(',')).join('\r\n');
        // BOM so Excel opens Vietnamese diacritics correctly rather than as mojibake.
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'phan-quyen.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    // Publish the exporter so the page header's button can fire it. Re-registered
    // whenever rows change, so the download always matches what is displayed.
    useEffect(() => {
        exportRef.current = downloadCsv;
        return () => {
            exportRef.current = null;
        };
    });

    // --- adapters onto the shared MatrixTable ------------------------------
    // The component speaks rows-and-columns; this screen speaks permissions and
    // roles. Everything below is that translation and nothing else — the moment
    // domain vocabulary leaks into the primitive it stops serving the next table.

    const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);

    const matrixColumns: MatrixColumn[] = useMemo(
        () =>
            columns.map((c) => ({
                key: c.key,
                // EAA-1843: ONE line. `meta` is deliberately not passed —
                // `MatrixTable` renders that second line only when it is
                // defined, so omitting it is what collapses the header rather
                // than any CSS hiding it.
                header: (
                    <span className="pm-th-role-name">
                        {c.label}
                        {c.locked && <LockIcon />}
                        {c.inactive && (
                            <span className="pm-th-role-off">
                                {t('console.permissions.catalog.roleOffBadge')}
                            </span>
                        )}
                    </span>
                ),
                locked: c.locked,
                width: 168,
                minWidth: 120,
                maxWidth: 360,
            })),
        [columns, t]
    );

    const matrixSections: MatrixSection[] = useMemo(
        () =>
            sections.map((section) => ({
                key: section.key,
                label: <span className="pm-group-name">{section.label}</span>,
                meta: (
                    <span className="pm-group-n">
                        {t('console.permissions.catalog.groupPermCount', {
                            count: section.rows.length,
                        })}
                    </span>
                ),
                rows: section.rows.map((p) => ({
                    // Keyed by CODE, not index: the row set is filtered and
                    // sorted, so an index would rebind ticks to other rows.
                    key: p.code,
                    // Plain data for the filter engine — `header` is what the
                    // cell shows, this is what the filter reads.
                    fields: { code: p.code, label: p.label, group: p.group },
                    header: (
                        <span className="pm-perm">
                            <span className="pm-perm-body">
                                <span className="pm-perm-label">{p.label}</span>
                                <code className="pm-perm-code">{p.code}</code>
                            </span>
                            <span className="pm-perm-flag">
                                {p.menu_only && (
                                    <span title={t('console.permissions.catalog.menuOnlyTooltip')}>
                                        <InfoIcon />
                                    </span>
                                )}
                            </span>
                            <span
                                className="pm-perm-n"
                                title={t('console.permissions.catalog.endpointCountTooltip')}
                            >
                                {p.endpoint_count}
                            </span>
                        </span>
                    ),
                })),
            })),
        [sections, t]
    );

    const sectionCodes = useMemo(
        () => new Map(sections.map((s) => [s.key, s.rows.map((r) => r.code)])),
        [sections]
    );

    /** Held-set lookup by column key, so `isChecked` stays O(1) per cell. */
    const heldByColumnKey = useCallback(
        (colKey: string) => {
            const col = byKey.get(colKey);
            return col ? held(col) : EMPTY_SET;
        },
        // `draft` is what makes a tick appear before it is saved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [byKey, draft]
    );


    if (query.isLoading || rolesQuery.isLoading) {
        return (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">
                    {t('console.permissions.catalog.loadingSrOnly')}
                </span>
                <SkeletonTable rows={8} columns={6} />
            </div>
        );
    }

    if (query.isError) {
        return (
            <ErrorState
                description={t('console.permissions.catalog.loadErrorDescription')}
                onRetry={() => void query.refetch()}
                retrying={query.isFetching}
            />
        );
    }
    return (
        <div className="pm">
            <div className="pm-toolbar">
                <div className="pm-toolbar-row">
                    <label className="pm-search">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('console.permissions.catalog.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label={t('console.permissions.catalog.searchAriaLabel')}
                        />
                    </label>
                    <span className="pm-spacer" />
                    <button
                        type="button"
                        className={hideBuiltin ? 'pm-pill pm-pill--on' : 'pm-pill'}
                        aria-pressed={hideBuiltin}
                        onClick={() => setHideBuiltin((v) => !v)}
                    >
                        {t('console.permissions.catalog.hideBuiltinToggle')}
                    </button>
                    {/* The group chips below ARE the filters, so this reports and
                        clears them rather than opening a second panel over the
                        same control. */}
                    <button
                        type="button"
                        className="pm-pill"
                        onClick={() => setGroup(null)}
                        disabled={group === null}
                    >
                        <FilterIcon />
                        {t('console.permissions.catalog.filterButton', { count: group ? 1 : 0 })}
                    </button>
                    <span className="pm-meta" aria-live="polite">
                        {t('console.permissions.catalog.metaSummary', {
                            rows: rows.length,
                            columns: columns.length,
                        })}
                    </span>
                </div>

                <div className="perm-chips" role="group" aria-label={t('console.permissions.catalog.groupFilterAriaLabel')}>
                    <button
                        type="button"
                        className={group === null ? 'perm-chip perm-chip--on' : 'perm-chip'}
                        aria-pressed={group === null}
                        onClick={() => setGroup(null)}
                    >
                        {t('console.permissions.catalog.allChipLabel')}{' '}
                        <span className="perm-chip-n">{permissions.length}</span>
                    </button>
                    {groups.map((g) => (
                        <button
                            key={g.key}
                            type="button"
                            className={group === g.key ? 'perm-chip perm-chip--on' : 'perm-chip'}
                            aria-pressed={group === g.key}
                            onClick={() => setGroup(group === g.key ? null : g.key)}
                        >
                            {g.label} <span className="perm-chip-n">{countByGroup.get(g.key) ?? 0}</span>
                        </button>
                    ))}
                </div>
            </div>

            <MatrixTable
                label={t('console.permissions.catalog.matrixLabel')}
                classNames={MATRIX_CLASSES}
                storageKey="permissions-matrix"
                rowHeaderWidth={440}
                stickyColumns={1}
                resizeLabel={(name) =>
                    t('console.permissions.catalog.resizeColumnAriaLabel', { name })
                }
                /* EAA-1843: ONE line. The caption that sat under this
                   ("việc được làm · mã quyền · số endpoint") legended the three
                   parts of each row cell — a label, a mono code and a count that
                   are self-evident once you read one row, so it cost a permanent
                   sticky line to explain something the first row already shows. */
                cornerHeader={
                    <button
                        type="button"
                        className="perm-sort"
                        onClick={() => setSorted((v) => (v === 'asc' ? 'desc' : 'asc'))}
                        aria-label={t('console.permissions.catalog.cornerSortAriaLabel')}
                    >
                        {t('console.permissions.catalog.cornerHeaderLabel')}
                        <CaretIcon up={sorted === 'desc'} />
                    </button>
                }
                columns={matrixColumns}
                sections={matrixSections}
                isChecked={(rowKey, colKey) => heldByColumnKey(colKey).has(rowKey)}
                onToggle={(rowKey, colKey) => toggle(byKey.get(colKey)!, rowKey)}
                onToggleSection={(sectionKey, colKey, next) => {
                    const codes = sectionCodes.get(sectionKey) ?? [];
                    toggleGroupCell(byKey.get(colKey)!, codes, !next);
                }}
                emptyMessage={t('console.permissions.catalog.emptyMessage')}
            />


            {/* Frame 62's save bar. Always mounted, never a layout jump when the
                first tick lands — it reports "no changes" instead of appearing. */}
            {canManage && (
                <div className="pm-savebar">
                    <span className={dirtyRoles.length ? 'pm-dirty' : 'pm-clean'}>
                        {dirtyRoles.length > 0 && <WarnIcon />}
                        {dirtyRoles.length === 0
                            ? t('console.permissions.catalog.noChangesLabel')
                            : dirtyRoles.length === 1
                              ? t('console.permissions.catalog.dirtySingleRole', { count: changeCount })
                              : t('console.permissions.catalog.dirtyMultiRoles', {
                                    count: changeCount,
                                    roles: dirtyRoles.length,
                                })}
                        {dirtyRoles.length === 1 && <strong>{dirtyRoles[0].name}</strong>}
                    </span>
                    <span className="pm-spacer" />
                    <button
                        type="button"
                        className="portal-btn pm-btn"
                        disabled={dirtyRoles.length === 0 || save.isPending}
                        onClick={() => setDraft({})}
                    >
                        {t('console.permissions.catalog.undoButton')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-primary pm-btn"
                        disabled={dirtyRoles.length === 0 || save.isPending}
                        onClick={() => save.mutate()}
                    >
                        {save.isPending
                            ? t('console.permissions.catalog.savingPending')
                            : t('console.permissions.catalog.saveButton')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default PermissionCatalogTab;
